'use client'

import {
  createContext,
  PropsWithChildren,
  Suspense,
  use,
  useContext,
  useEffect,
  useState,
} from 'react'

import { RoomTag } from '@/types/genre'

type GenreContextType = {
  tags: RoomTag[]
}

const GenreContext = createContext<GenreContextType | null>(null)

type GenreProviderProps = PropsWithChildren<{
  tagsPromise: Promise<RoomTag[]>
}>

function TagsSuspender({
  promise,
  onLoad,
}: {
  promise: Promise<RoomTag[]>
  onLoad: (tags: RoomTag[]) => void
}) {
  const tags = use(promise)

  useEffect(() => {
    onLoad(tags)
  }, [onLoad, tags])

  return null
}

export function GenreProvider({ children, tagsPromise }: GenreProviderProps) {
  const [tags, setTags] = useState<RoomTag[]>([])

  return (
    <GenreContext.Provider value={{ tags }}>
      {children}
      <Suspense fallback={null}>
        <TagsSuspender promise={tagsPromise} onLoad={setTags} />
      </Suspense>
    </GenreContext.Provider>
  )
}

export const useGenreContext = () => {
  const value = useContext(GenreContext)

  if (value == null) {
    throw new Error('useGenreContext cannot be used outside of GenreProvider')
  }

  return value
}
